export interface FiatPrice {
  currencyCode: string;
  currencySymbol: string;
  value: number;
}

export interface RewardsPrice {
  currency: string;
  value: number;
}

export interface AccountPrice {
  currency: string;
  value: number;
  currencyDescription?: string;
}

export interface Prices {
  fiat: FiatPrice;
  accountSpecific: { [key: string]: AccountPrice };
  rewards?: { [key: string]: RewardsPrice };
}

export const getRewardsPrice = (prices: Prices, accountId?: string) => {
  if (accountId != null) {
    return prices.accountSpecific[accountId];
  } else {
    return Object.values(prices.accountSpecific)?.[0];
  }
};

/** Checks if two FiatPrice objects are equal by comparing currency code and value. */
export const checkIsFiatEqual = <T extends FiatPrice | null | undefined>(
  fiat1: T,
  fiat2: T
): boolean => {
  if (fiat1 == null || fiat2 == null) {
    // eslint-disable-next-line eqeqeq -- intentional == to treat null and undefined as equal
    return fiat1 == fiat2;
  }
  return (
    fiat1.currencyCode === fiat2.currencyCode && fiat1.value === fiat2.value
  );
};

/** Adds monetary values using integer cents arithmetic to avoid floating-point errors. */
const addMonetaryValues = (a: number, b: number): number => {
  const centsA = Math.round(a * 100);
  const centsB = Math.round(b * 100);
  return (centsA + centsB) / 100;
};

/** Negates all values in a Prices object. Useful for subtraction via addPrices(a, negatePrices(b)). */
export const negatePrices = (prices: Prices): Prices => ({
  fiat: {
    ...prices.fiat,
    value: -prices.fiat.value,
  },
  accountSpecific: Object.keys(prices.accountSpecific).reduce(
    (acc, key) => {
      acc[key] = {
        ...prices.accountSpecific[key],
        value: -prices.accountSpecific[key].value,
      };
      return acc;
    },
    {} as Prices["accountSpecific"]
  ),
  ...(prices.rewards && {
    rewards: Object.keys(prices.rewards).reduce(
      (acc, key) => {
        acc[key] = {
          ...prices.rewards![key],
          value: -prices.rewards![key].value,
        };
        return acc;
      },
      {} as NonNullable<Prices["rewards"]>
    ),
  }),
});

/** Adds two Prices objects together. Throws if currencies or account keys don't match. */
export const addPrices = (first: Prices, second: Prices): Prices => {
  if (first.fiat.currencyCode !== second.fiat.currencyCode) {
    throw new Error(
      `Currency mismatch in fiat prices: not all prices have the same currency`
    );
  }

  const firstKeys = Object.keys(first.accountSpecific).sort();
  const secondKeys = Object.keys(second.accountSpecific).sort();

  if (JSON.stringify(firstKeys) !== JSON.stringify(secondKeys)) {
    throw new Error(
      `AccountSpecific keys mismatch: not all prices have the same account keys`
    );
  }

  const allAccountCurrenciesMatch = firstKeys.every(
    (accountId) =>
      first.accountSpecific[accountId].currency ===
      second.accountSpecific[accountId].currency
  );
  if (!allAccountCurrenciesMatch) {
    throw new Error(
      `Currency mismatch in accountSpecific: not all prices have the same currency for all accounts`
    );
  }

  const fiat: FiatPrice = {
    currencyCode: first.fiat.currencyCode,
    currencySymbol: first.fiat.currencySymbol,
    value: addMonetaryValues(first.fiat.value, second.fiat.value),
  };

  const accountSpecific = firstKeys.reduce(
    (acc, accountId) => {
      acc[accountId] = {
        currency: first.accountSpecific[accountId].currency,
        value: addMonetaryValues(
          first.accountSpecific[accountId].value,
          second.accountSpecific[accountId].value
        ),
        currencyDescription:
          first.accountSpecific[accountId].currencyDescription,
      };

      return acc;
    },
    {} as Prices["accountSpecific"]
  );

  return {
    fiat,
    accountSpecific,
  };
};

/** Subtracts second Prices from first Prices. Equivalent to addPrices(first, negatePrices(second)). */
export const subtractPrices = (first: Prices, second: Prices): Prices =>
  addPrices(first, negatePrices(second));

/** Multiplies all values in a Prices object by a scalar. */
export const multiplyPrices = (prices: Prices, multiplier: number): Prices => ({
  fiat: {
    ...prices.fiat,
    value: prices.fiat.value * multiplier,
  },
  accountSpecific: Object.keys(prices.accountSpecific).reduce(
    (acc, key) => {
      acc[key] = {
        ...prices.accountSpecific[key],
        value: prices.accountSpecific[key].value * multiplier,
      };
      return acc;
    },
    {} as Prices["accountSpecific"]
  ),
});
